import { Add, Delete, Edit } from "@mui/icons-material";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SysColumn from "app/class/sys-column";
import { FilterBuilder } from "modules/record/record-producer/components/record-producer-fields/condition-field/FilterBuilder";
import { OPERATORS_BY_TYPE } from "modules/record/record-producer/components/record-producer-fields/condition-field/utils/operators.config";
import { LogicalNode, QueryNode } from "modules/record/record-producer/types/query.types";
import { resolveDisplayText } from "modules/record/shared/services/recordMetadataLabels";
import { BuilderFieldDef, BuilderFieldRules } from "../../types/formBuilder.types";
import { compileRuleCondition, parseRuleCondition, SUPPORTED_FILTER_OPERATORS } from "../../services/ruleConditionCodec";

interface RuleBuilderProps {
    rules: BuilderFieldRules;
    fields: BuilderFieldDef[];
    onChange: (rules: Partial<BuilderFieldRules>) => void;
}
type RuleMode = "conditions" | "always" | "never";
const RULE_FIELDS: Array<{ key: keyof BuilderFieldRules; label: string }> = [
    { key: "visible", label: "Visible" }, { key: "required", label: "Required" },
    { key: "readonly", label: "Read-only" }, { key: "hidden", label: "Hidden" },
    { key: "disabled", label: "Disabled" }, { key: "label", label: "Label" },
];
const COLUMN_TYPES: Record<string, string> = {
    text: "TEXT", textarea: "TEXT", number: "INTEGER", decimal: "DECIMAL", currency: "DECIMAL",
    date: "DATE", datetime: "DATETIME", time: "TIME", boolean: "BOOLEAN", choice: "CHOICE",
    reference: "TEXT", richtext: "TEXT", attachment: "TEXT", password: "TEXT", color: "TEXT", email: "TEXT", url: "TEXT",
};
const emptyNode = (): LogicalNode => ({ id: "rule-root", type: "logical", operator: "and", conditions: [{ id: "rule-empty", type: "condition", field: "", operator: "" }] });
const ruleModeFor = (value: string): RuleMode => value.trim() === "true" ? "always" : value.trim() === "false" ? "never" : "conditions";

const RuleBuilder = ({ rules, fields, onChange }: RuleBuilderProps) => {
    const { t } = useTranslation();
    const [editingRule, setEditingRule] = useState<keyof BuilderFieldRules | null>(null);
    const [ruleMode, setRuleMode] = useState<RuleMode>("conditions");
    const [draft, setDraft] = useState<LogicalNode>(emptyNode);
    const [error, setError] = useState("");
    const columns = useMemo(() => fields.map((field) => {
        const column = new SysColumn();
        column.id = field.name;
        column.name = field.name;
        column.label = String(resolveDisplayText(t, field.label) || field.name);
        column.column_type = COLUMN_TYPES[field.type] || "TEXT";
        column.options = field.type === "choice" ? field.choiceItems?.map((choice) => ({ value: choice.value, label: choice.label })) || [] : undefined;
        return column;
    }), [fields, t]);
    const editingDefinition = RULE_FIELDS.find((rule) => rule.key === editingRule);

    const openEditor = (key: keyof BuilderFieldRules) => {
        const value = rules[key] || "";
        setEditingRule(key);
        setRuleMode(key === "label" ? "conditions" : ruleModeFor(value));
        setError("");
        try { setDraft(value && ruleModeFor(value) === "conditions" ? parseRuleCondition(value) : emptyNode()); }
        catch { setDraft(emptyNode()); setError(String(t("Unable to read this rule."))); }
    };
    const saveEditor = () => {
        if (!editingRule) return;
        try {
            const value = editingRule !== "label" && ruleMode !== "conditions"
                ? String(ruleMode === "always") : compileRuleCondition(draft);
            onChange({ [editingRule]: value });
            setEditingRule(null);
        } catch { setError(String(t("Complete the rule before saving."))); }
    };
    const summaryNode = (node: QueryNode, nested = false): string => {
        if (node.type === "logical") {
            const summary = node.conditions.map((child) => summaryNode(child, true)).filter(Boolean).join(` ${t(node.operator === "and" ? "and" : "or")} `);
            return nested && node.conditions.length > 1 ? `(${summary})` : summary;
        }
        if (node.type === "exists") return "";
        const field = columns.find((column) => column.name === node.field);
        const label = field?.label || node.field;
        const operator = OPERATORS_BY_TYPE[field?.column_type || "TEXT"]?.find((item) => item.value === node.operator)?.label || node.operator;
        if (node.operator === "eq" && typeof node.value === "boolean") return `${label} ${t("is")} ${t(String(node.value))}`;
        return `${label} ${t(operator)}${node.operator === "empty" || node.operator === "not_empty" ? "" : ` ${String(node.value ?? "")}`}`;
    };
    const formatSummary = (value: string) => {
        if (value === "true") return t("Always active");
        if (value === "false") return t("Never active");
        try { return summaryNode(parseRuleCondition(value)); } catch { return value; }
    };

    return <Box className="service-builder__rule-builder">
        <Typography className="service-builder__section-title">{t("Rules")}</Typography>
        {RULE_FIELDS.map((rule) => {
            const active = Boolean(rules[rule.key]);
            const summary = active ? String(formatSummary(rules[rule.key])) : String(t("No rule configured"));
            return <Box key={rule.key} className="service-builder__rule-summary"><Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Box minWidth={0}><Typography variant="subtitle2">{t(rule.label)}</Typography>
                    <Typography className="service-builder__rule-expression" noWrap title={summary} aria-label={summary}>{summary}</Typography></Box>
                <Stack direction="row" spacing={0.5}>
                    <Tooltip title={active ? t("Edit rule") : t("Add rule")}><IconButton size="small" onClick={() => openEditor(rule.key)}>{active ? <Edit fontSize="small" /> : <Add fontSize="small" />}</IconButton></Tooltip>
                    <Tooltip title={t("Remove rule")}><span><IconButton size="small" disabled={!active} onClick={() => onChange({ [rule.key]: "" })}><Delete fontSize="small" /></IconButton></span></Tooltip>
                </Stack>
            </Stack></Box>;
        })}
        <Dialog open={Boolean(editingRule)} onClose={() => setEditingRule(null)} fullWidth maxWidth="md">
            <DialogTitle>{editingDefinition ? t(editingDefinition.label) : t("Rule")}</DialogTitle>
            <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
                {editingRule !== "label" && <TextField size="small" select label={t("Rule mode")} value={ruleMode} onChange={(event) => setRuleMode(event.target.value as RuleMode)} sx={{ maxWidth: 260 }}>
                    <MenuItem value="conditions">{t("Conditional")}</MenuItem><MenuItem value="always">{t("Always active")}</MenuItem><MenuItem value="never">{t("Never active")}</MenuItem>
                </TextField>}
                {error && <Alert severity="error">{error}</Alert>}
                {ruleMode === "conditions" && <FilterBuilder value={draft} onChange={setDraft} columns={columns} allColumns={columns} tables={[]}
                    flatFields guided fieldAriaLabel={String(t("Field"))} andLabel={String(t("AND"))} orLabel={String(t("OR"))} allowedOperators={SUPPORTED_FILTER_OPERATORS} allowExists={false} />}
            </Stack></DialogContent>
            <DialogActions><Button onClick={() => setEditingRule(null)}>{t("Cancel")}</Button><Button variant="contained" onClick={saveEditor}>{t("Apply")}</Button></DialogActions>
        </Dialog>
    </Box>;
};
export default RuleBuilder;
